import AlertDialog from "@/src/components/atom/AlertDialog";
import { Button } from "@/src/components/atom/Button";
import Dialog from "@/src/components/atom/Dialog";
import DialogFooterContainer from "@/src/components/atom/Dialog/DialogFooterContainer";
import Input from "@/src/components/atom/Input";
import Table from "@/src/components/atom/Table";
import Typo from "@/src/components/atom/Typo";
import { InputError } from "@/src/components/molecule/FormItem";
import useAgGridHeaderRefresh from "@/src/hooks/useAgGridHeaderRefresh";
import useAlert from "@/src/hooks/useAlert";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import IMPORTER_PRIVATE_PATH from "@/src/routes/importer/path";
import {
  buyerApi,
  useInvalidatesBuyerMutation,
  useLazyCheckClientNameCodeQuery,
} from "@/src/store/apis/client/buyer";
import { useGetCommonCodeViaCodeNameQuery } from "@/src/store/apis/common";
import { PartialCommonCodeItemDto } from "@/src/store/apis/common/interface";
import {
  useApproveLinkMutation,
  useLazyGetCorporateManagerInfoViaLinkCodeQuery,
  useRejectLinkMutation,
} from "@/src/store/apis/link";
import { BuyerEmployeeDtoForSharing } from "@/src/store/apis/link/interface";
import colorSet from "@/src/styles/color";
import { isUndefined } from "@/src/utils/is";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  clientCorporateManagerColumn,
  clientManagerColumn,
  staffManagerColumn,
} from "./columns/column";
import {
  useGetStaffsQuery,
  useLazyGetStaffsQuery,
} from "@/src/store/apis/corporate/staffManagement";
import { RowSelectedEvent } from "ag-grid-community";
import { calculateTableHeight } from "@/src/utils/calculateTableHeight";

interface LinkApproveDialogProps {
  onClose: () => void;
  defaultClientCode: string;
  clientId: number;
  type: "BUYER" | "SELLER";
}

enum AlertDialogState {
  NULL,
  REJECT,
}

const EMPTY_ARRAY: PartialCommonCodeItemDto[] = [];
const EMPTY_EMPLOYEE_ARRAY: BuyerEmployeeDtoForSharing[] = [];
const EMPTY_STRING_ARRAY: string[] = [];

const LinkApproveDialog = ({
  onClose,
  defaultClientCode,
  clientId,
  type,
}: LinkApproveDialogProps) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const navigate = useNavigate();
  const {
    formState: { errors },
    control,
    setValue,
    watch,
    getValues,
  } = useForm<{ clientCode: string }>({
    mode: "onBlur",
    defaultValues: {
      clientCode: defaultClientCode,
    },
  });

  const isClientStaffPageChanging = useRef(false);
  const isPageChanging = useRef(false);
  const corpManagerGridRef = useRef<AgGridReact>(null);
  const clientGridRef = useRef<AgGridReact>(null);
  const staffManagerGridRef = useRef<AgGridReact>(null);

  const [isCorpManagerGridReady, setIsCorpManagerGridReady] =
    useState<boolean>(false);
  const [isClientManagerGridReady, setIsClientManagerGridReady] =
    useState<boolean>(false);
  const [isStaffManagerGridReady, setStaffManagerIsGridReady] =
    useState<boolean>(false);
  const [clientPagination, setClientPagination] = useState(1);
  const [staffPagination, setStaffPagination] = useState(1);
  const [selectedStaffList, setSelectedStaffList] = useState<number[]>([]);

  const [alertDialogState, setAlertDialogState] = useState<AlertDialogState>(
    AlertDialogState.NULL
  );

  const [column] = useState(clientCorporateManagerColumn);
  const [clientColumn] = useState(clientManagerColumn);
  const [staffColumn] = useState(staffManagerColumn);
  const [invalidatesBuyer] = useInvalidatesBuyerMutation();
  const { mainCategory } = useGetCommonCodeViaCodeNameQuery(
    {
      codeName: "MAIN_CATEGORY",
    },
    {
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isError || isFetching || isUndefined(currentData);

        return {
          mainCategory: !isUnstable ? currentData : EMPTY_ARRAY,
        };
      },
    }
  );

  const [getStaffs, { isError: isOnePageManagerListError }] =
    useLazyGetStaffsQuery();
  const {
    isManagerListError,
    isManagerListFetching,
    managerList,
    managerListCount,
  } = useGetStaffsQuery(
    {
      isActivated: true,
      page: staffPagination,
      pageSize: 5,
      exporterUserTypes: ["MANAGER", "MIDDLE_MANAGER"],
    },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ isError, isFetching, currentData }) => {
        const isUnstable = isError || isFetching || isUndefined(currentData);
        const isStable = !isUnstable;
        return {
          isManagerListError: isError,
          isManagerListFetching: isFetching,
          managerList: isStable ? currentData.rows : EMPTY_ARRAY,
          managerListCount: isStable ? currentData.count : 0,
        };
      },
    }
  );

  const [checkClientCode] = useLazyCheckClientNameCodeQuery();
  const [approve, { isLoading: isApproving }] = useApproveLinkMutation();
  const [reject] = useRejectLinkMutation();
  const [
    getSenderCorporateManagerInfoViaLinkCode,
    {
      isFetching: isFetchingSenderCorporateInfoViaLinkCode,
      currentData: senderCorporateInfo,
    },
  ] = useLazyGetCorporateManagerInfoViaLinkCodeQuery();

  const {
    buyerEmployeeDtoForSharingList,
    client,
    contact,
    country,
    businessNumber,
    mainCategoryCode,
    address,
    sender,
    isSenderFetching,
  } = buyerApi.endpoints.getBuyer.useQueryState(
    {
      id: clientId,
    },
    {
      selectFromResult: ({ currentData, isSuccess, isFetching }) => {
        return {
          isSenderFetching: isFetching,
          sender: isSuccess
            ? currentData?.buyerQueryResultDto.companyLinkageHistory.sender
            : undefined,
          buyerEmployeeDtoForSharingList: isSuccess
            ? currentData?.buyerQueryResultDto.companyLinkageHistory
                .buyerEmployeeDtoForSharingList
            : EMPTY_EMPLOYEE_ARRAY,
          totalPage: isSuccess
            ? currentData?.buyerQueryResultDto.companyLinkageHistory
                .buyerEmployeeDtoForSharingList.length
            : 0,
          client: isSuccess
            ? currentData?.buyerQueryResultDto.companyLinkageHistory.sender
                ?.companyName
            : "-",
          clientType: isSuccess
            ? currentData?.buyerQueryResultDto.companyLinkageHistory.sender
                ?.companyType
            : undefined,
          contact: isSuccess
            ? `${
                currentData?.buyerQueryResultDto.companyLinkageHistory.sender
                  ?.telPrefix || ""
              } ${
                currentData?.buyerQueryResultDto.companyLinkageHistory.sender
                  ?.tel || ""
              }`
            : "-",
          country: isSuccess
            ? currentData?.buyerQueryResultDto.companyLinkageHistory.sender
                ?.country
            : "-",
          businessNumber: isSuccess
            ? currentData?.buyerQueryResultDto.companyLinkageHistory.sender
                ?.businessNumber
            : "-",
          mainCategoryCode: isSuccess
            ? currentData?.buyerQueryResultDto.companyLinkageHistory.sender
                ?.mainCategoryCodeItemNames || EMPTY_STRING_ARRAY
            : EMPTY_STRING_ARRAY,
          address: isSuccess
            ? [
                currentData?.buyerQueryResultDto.companyLinkageHistory.sender
                  ?.postalCode,
                currentData?.buyerQueryResultDto.companyLinkageHistory.sender
                  ?.streetAddress,
                currentData?.buyerQueryResultDto.companyLinkageHistory.sender
                  ?.locality,
                currentData?.buyerQueryResultDto.companyLinkageHistory.sender
                  ?.region,
                currentData?.buyerQueryResultDto.companyLinkageHistory.sender
                  ?.countryName,
              ]
                .filter((addressPartial) => !!addressPartial)
                .join(", ")
            : "-",
        };
      },
    }
  );

  const senderCorporateManager = buyerEmployeeDtoForSharingList?.slice(
    clientPagination * 5 - 5,
    clientPagination * 5
  );
  const filteredSenderCorporateManager = senderCorporateManager?.filter(
    (staff) => staff.email !== senderCorporateInfo?.row.email
  );
  const filteredSenderStaffCount =
    buyerEmployeeDtoForSharingList?.filter(
      (staff) => staff.email !== senderCorporateInfo?.row.email
    ).length || 0;

  const filteredSenderCorporateManagerCount =
    filteredSenderCorporateManager?.length || 0;

  const mainCategoryCapitalize = mainCategoryCode
    .map(
      (itemCode) =>
        mainCategory.find(({ codeItemName }) => codeItemName === itemCode)
          ?.codeItemNameEn
    )
    .join(", ");

  const handleStaffPaginationClick = (page: number) => {
    isPageChanging.current = true;
    setStaffPagination(page);
  };

  const handleClientStaffPaginationClick = (page: number) => {
    isClientStaffPageChanging.current = true;
    setClientPagination(page);
  };

  // AgGrid Checkbox
  const handleSelectionChanged = (e: RowSelectedEvent) => {
    if (
      e.source === "rowClicked" ||
      e.source === "uiSelectAll" ||
      e.source === "checkboxSelected"
    ) {
      const selectedNodesData = e.api.getSelectedNodes();
      const accountKey = e.node.data.exporterUserListQueryResultDto.id;
      const keys = selectedNodesData.map(
        (item) => item.data.exporterUserListQueryResultDto.id
      ) as number[];

      if (selectedStaffList.includes(accountKey)) {
        setSelectedStaffList((prev) => prev.filter((id) => id !== accountKey));
      } else {
        const set: any = new Set([...selectedStaffList, ...keys]);
        setSelectedStaffList([...set]);
      }
    }
  };

  // AgGrid 페이지 네이션
  const handleRowDataUpdated = () => {
    const isLiveGridRef = !!staffManagerGridRef.current;

    if (isLiveGridRef) {
      staffManagerGridRef.current?.api.forEachNode((rowNode) => {
        if (
          selectedStaffList.includes(
            rowNode.data.exporterUserListQueryResultDto.id
          )
        ) {
          rowNode.setSelected(true);
        }
      });
    }
  };

  const handleApprove = async () => {
    try {
      await approve({
        id: clientId,
        companyType: "BOTH",
        buyerNameCode: getValues("clientCode"),
        exporterUserIdList: selectedStaffList,
      }).unwrap();

      alert.showAlert({
        type: "success",
        message: t("clientManagement:exporter.linkApprove.alert.success"),
      });

      invalidatesBuyer();
      onClose();
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleReject = async () => {
    try {
      await reject({
        id: clientId,
      }).unwrap();

      setAlertDialogState(AlertDialogState.NULL);
      onClose();

      if (type === "BUYER") {
        return navigate(IMPORTER_PRIVATE_PATH.CLIENT_MANAGEMENT, {
          replace: true,
        });
      }
      if (type === "SELLER") {
        return navigate(EXPORTER_PRIVATE_PATH.CLIENT_MANAGEMENT, {
          replace: true,
        });
      }
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  useAgGridHeaderRefresh({
    gridRef: corpManagerGridRef.current,
    isReady: isCorpManagerGridReady,
    headerSet: [
      {
        columnKey: "name",
        langKey: "clientManagement:exporter.common.name",
      },
      {
        columnKey: "aId",
        langKey: "clientManagement:exporter.common.email",
      },
      {
        columnKey: "personalContact",
        langKey: "clientManagement:exporter.common.personalContact",
      },
      {
        columnKey: "officeContact",
        langKey: "clientManagement:exporter.common.officeContact",
      },
    ],
  });

  useAgGridHeaderRefresh({
    gridRef: clientGridRef.current,
    isReady: isClientManagerGridReady,
    headerSet: [
      {
        columnKey: "name",
        langKey: "clientManagement:exporter.common.name",
      },
      {
        columnKey: "email",
        langKey: "clientManagement:exporter.common.email",
      },
      {
        columnKey: "personalContact",
        langKey: "clientManagement:exporter.common.personalContact",
      },
      {
        columnKey: "officeContact",
        langKey: "clientManagement:exporter.common.officeContact",
      },
    ],
  });

  useAgGridHeaderRefresh({
    gridRef: staffManagerGridRef.current,
    isReady: isStaffManagerGridReady,
    headerSet: [
      {
        columnKey: "name",
        langKey: "clientManagement:exporter.common.name",
      },
      {
        columnKey: "email",
        langKey: "clientManagement:exporter.common.email",
      },
      {
        columnKey: "personalContact",
        langKey: "clientManagement:exporter.common.personalContact",
      },
      {
        columnKey: "officeContact",
        langKey: "clientManagement:exporter.common.officeContact",
      },
    ],
  });

  const clientStaffListTableHeight = useMemo(() => {
    return calculateTableHeight({
      count: isClientStaffPageChanging.current
        ? 5
        : filteredSenderCorporateManagerCount,
    });
  }, [filteredSenderCorporateManagerCount]);

  const renderClientCorpInfo = () => {
    return (
      <InfoBox>
        <Box>
          <InfoRow>
            <InfoLabel>
              <Typo typoType="b7m">
                {t(
                  "clientManagement:exporter.linkCompanyDetailDialog.clientInfo.client"
                )}
              </Typo>
            </InfoLabel>
            <InfoValue>
              <Typo typoType="b7r" color="gray5">
                {client}
              </Typo>
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>
              {t(
                "clientManagement:exporter.linkCompanyDetailDialog.clientInfo.clientCode"
              )}
            </InfoLabel>
            <InfoValue className="client-code-input">
              <Controller
                control={control}
                name="clientCode"
                rules={{
                  maxLength: 80,
                  required: true,
                  validate: async (buyerNameCode) => {
                    if (buyerNameCode.length > 80) return true;
                    const { isError } = await checkClientCode({
                      buyerNameCode,
                    });
                    return !isError;
                  },
                }}
                render={({ field }) => {
                  return (
                    <Input
                      data-invalid={!!errors.clientCode}
                      onClear={() => setValue("clientCode", "")}
                      {...field}
                    />
                  );
                }}
              />
              {errors.clientCode && (
                <StyledInputError
                  message={(() => {
                    switch (errors.clientCode.type) {
                      case "maxLength":
                        return t("error:maxLength80");
                      case "validate":
                        return t("error:alreadyExistedClientCode");
                      case "required":
                        return t("error:required");
                    }
                  })()}
                />
              )}
            </InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>
              <Typo typoType="b7m">
                {t(
                  "clientManagement:exporter.linkCompanyDetailDialog.clientInfo.country"
                )}
              </Typo>
            </InfoLabel>
            <InfoValue>
              <Typo typoType="b7r" color="gray5">
                {country}
              </Typo>
            </InfoValue>
          </InfoRow>
          {[
            {
              label: t(
                "clientManagement:exporter.linkCompanyDetailDialog.clientInfo.businessNumber"
              ),
              value: businessNumber,
            },
            {
              label: t(
                "clientManagement:exporter.linkCompanyDetailDialog.clientInfo.mainCategory"
              ),
              value: mainCategoryCapitalize,
            },
            {
              label: t(
                "clientManagement:exporter.linkCompanyDetailDialog.clientInfo.contact"
              ),
              value: contact,
            },
            {
              label: t(
                "clientManagement:exporter.linkCompanyDetailDialog.clientInfo.address"
              ),
              value: address,
            },
          ].map(({ label, value }) => {
            return (
              <InfoRow>
                <InfoLabel>
                  <Typo typoType="b7m">{label}</Typo>
                </InfoLabel>
                <InfoValue>
                  <Typo typoType="b7r" color="gray5">
                    {value}
                  </Typo>
                </InfoValue>
              </InfoRow>
            );
          })}
        </Box>
      </InfoBox>
    );
  };

  const renderClientCorpManagerInfo = () => {
    return (
      <Article>
        <Typo typoType="b7m">
          {t(
            "clientManagement:exporter.linkCompanyDetailDialog.clientCorporateManager"
          )}
        </Typo>
        <TableWrapper>
          <Table
            ref={corpManagerGridRef}
            columnDefs={column}
            isPagination={false}
            height={85}
            rowData={
              isFetchingSenderCorporateInfoViaLinkCode || !senderCorporateInfo
                ? undefined
                : [senderCorporateInfo.row]
            }
            onGridReady={() => {
              setIsCorpManagerGridReady(true);
            }}
          />
        </TableWrapper>
      </Article>
    );
  };

  const renderClientStaffList = () => {
    return (
      <Article>
        <Typo typoType="b7m">
          {t("clientManagement:exporter.linkApprove.staffList.list")}
        </Typo>

        <TableWrapper>
          <Table
            ref={clientGridRef}
            columnDefs={clientColumn}
            isPaginationDataMaping
            height={clientStaffListTableHeight}
            totalPage={filteredSenderStaffCount}
            page={clientPagination}
            pageSize={5}
            domLayout={
              isClientStaffPageChanging.current ||
              filteredSenderCorporateManagerCount > 5
                ? undefined
                : "normal"
            }
            handlePaginationClick={handleClientStaffPaginationClick}
            rowData={
              isSenderFetching ||
              isUndefined(filteredSenderCorporateManager) ||
              isUndefined(senderCorporateInfo)
                ? undefined
                : filteredSenderCorporateManager || []
            }
            onGridReady={() => {
              setIsClientManagerGridReady(true);
            }}
          />
        </TableWrapper>
      </Article>
    );
  };

  const renderCompanyStaffList = () => {
    return (
      <Article>
        <Typo typoType="b7m">
          {t("clientManagement:exporter.linkCompanyDetailDialog.staffList")}{" "}
        </Typo>
        <TableWrapper>
          <Table
            ref={staffManagerGridRef}
            columnDefs={staffColumn}
            height={calculateTableHeight({
              count: isPageChanging.current ? 5 : managerListCount,
            })}
            rowSelection={"multiple"}
            rowMultiSelectWithClick={true}
            totalPage={managerListCount}
            page={staffPagination}
            pageSize={5}
            domLayout={
              isPageChanging.current || managerListCount > 5
                ? undefined
                : "normal"
            }
            handlePaginationClick={handleStaffPaginationClick}
            onRowSelected={handleSelectionChanged}
            onRowDataUpdated={handleRowDataUpdated}
            rowData={
              isManagerListFetching
                ? undefined
                : isManagerListError || isUndefined(managerList)
                  ? []
                  : managerList
            }
            onGridReady={() => {
              setStaffManagerIsGridReady(true);
            }}
          />
        </TableWrapper>
      </Article>
    );
  };

  const renderAlertDialog = () => {
    if (alertDialogState === AlertDialogState.REJECT) {
      return (
        <AlertDialog
          open
          title={t(
            "clientManagement:exporter.linkApprove.alertDialog.reject.title"
          )}
          onOpenChange={() => {
            setAlertDialogState(AlertDialogState.NULL);
          }}
          onOk={handleReject}
          cancelText={t("common:cancel")}
          okText={t("common:ok")}
        >
          {t(
            "clientManagement:exporter.linkApprove.alertDialog.reject.description"
          )}
        </AlertDialog>
      );
    }
  };

  useEffect(() => {
    setValue("clientCode", defaultClientCode);
  }, [defaultClientCode, setValue]);

  useEffect(() => {
    if (!isManagerListFetching && isPageChanging.current) {
      isPageChanging.current = false;
    }
  }, [isManagerListFetching]);

  useEffect(() => {
    if (!isSenderFetching && isClientStaffPageChanging.current) {
      isClientStaffPageChanging.current = false;
    }
  }, [isSenderFetching]);

  useEffect(() => {
    (async () => {
      await getStaffs({
        isActivated: true,
        page: 1,
        pageSize: 5,
        exporterUserTypes: ["MANAGER", "MIDDLE_MANAGER"],
      });
    })();
  }, [getStaffs]);

  useEffect(() => {
    if (sender?.companyLinkCode) {
      (async () => {
        await getSenderCorporateManagerInfoViaLinkCode({
          companyLinkCode: sender?.companyLinkCode,
        }).unwrap();
      })();
    }
  }, [sender?.companyLinkCode, getSenderCorporateManagerInfoViaLinkCode]);

  if (isSenderFetching || isFetchingSenderCorporateInfoViaLinkCode) {
    return null;
  }

  return (
    <Dialog
      title={t("clientManagement:exporter.linkApprove.dialogTitle")}
      open
      onOpenChange={onClose}
      width={1000}
      footer={
        <DialogFooterContainer>
          <Button
            buttonGrade={"tertiary"}
            buttonColor="black"
            onClick={() => setAlertDialogState(AlertDialogState.REJECT)}
          >
            {t("clientManagement:exporter.linkApprove.buttons.reject")}
          </Button>
          <Button
            disabled={
              !watch("clientCode") || !!errors.clientCode || isApproving
            }
            onClick={handleApprove}
            isLoading={isApproving}
          >
            {t("clientManagement:exporter.linkApprove.buttons.ok")}
          </Button>
        </DialogFooterContainer>
      }
    >
      <TextContainer>
        <PreLineTypo typoType="h2">
          {t("clientManagement:exporter.linkApprove.title")}
        </PreLineTypo>
        <PreLineTypo typoType="b7m">
          {t("clientManagement:exporter.linkApprove.description")}
        </PreLineTypo>
      </TextContainer>
      <InnerContainer>
        {renderClientCorpInfo()}
        {renderClientCorpManagerInfo()}
        {!!filteredSenderCorporateManagerCount && renderClientStaffList()}
        {(!isOnePageManagerListError || isPageChanging.current) &&
          renderCompanyStaffList()}
      </InnerContainer>

      {renderAlertDialog()}
    </Dialog>
  );
};

export default LinkApproveDialog;

const InfoBox = styled.div`
  border: 1px solid ${colorSet.gray9};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  gap: 16px;
`;

const Box = styled.div`
  width: calc(100% - 8px);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const InfoLabel = styled.div`
  width: 164px;
`;
const InfoValue = styled.div`
  flex: 1;

  &.client-code-input div {
    width: 248px;
  }
`;

const TextContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 40px;
`;

const InnerContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledInputError = styled(InputError)`
  padding-top: 8px;
`;

const Article = styled.article`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TableWrapper = styled.div`
  border: 1px solid ${colorSet.gray9};
  border-radius: 8px;

  .ant-pagination-options {
    display: none;
  }
`;

const PreLineTypo = styled(Typo)`
  white-space: pre-line;
  text-align: center;
`;
